/**
 * LeagueWebsitePage.js
 * Admin UI for configuring a league's public website.
 * Accessed from Dashboard "Website" button on each league card.
 *
 * Sections:
 *  - Website Setup: subdomain claim, enable/disable toggle, "View Website" link
 *  - Theme Editor: color pickers with live header preview, logo/banner URL inputs
 *  - Sections Manager: enable/disable toggles + up/down reorder
 *  - About Editor: textarea + plain text preview
 *  - Announcements Manager: list with pinned badge, new/edit/delete
 *  - Sponsors Manager: list with tier badge, add/edit/delete
 */

import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';
import UpgradePrompt from './UpgradePrompt';
import './LeagueWebsitePage.css';

// ─── Constants ──────────────────────────────────────────────────────────────

const SECTION_LABELS = {
  schedule:     'Schedule',
  standings:    'Standings',
  registration: 'Registration',
  rosters:      'Rosters',
  news:         'News',
  photos:       'Photos',
  sponsors:     'Sponsors',
  about:        'About',
};

const TIER_LABELS = { gold: 'Gold', silver: 'Silver', bronze: 'Bronze' };
const TIER_COLORS = { gold: '#f59e0b', silver: '#94a3b8', bronze: '#b45309' };

const DEFAULT_THEME = {
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  backgroundColor: '#ffffff',
  textColor: '#111827',
  logoUrl: null,
  bannerUrl: null,
};

const DEFAULT_SECTIONS = {
  schedule:     { enabled: true,  order: 1 },
  standings:    { enabled: true,  order: 2 },
  registration: { enabled: false, order: 3 },
  rosters:      { enabled: false, order: 4 },
  news:         { enabled: true,  order: 5 },
  photos:       { enabled: false, order: 6 },
  sponsors:     { enabled: false, order: 7 },
  about:        { enabled: false, order: 8 },
};

// ─── ImageUploader ────────────────────────────────────────────────────────────

/**
 * Reusable image uploader with drag-and-drop, preview, and remove.
 * Props:
 *   onUpload(url, key) - called after a successful upload
 *   onRemove()         - called when the user removes the current image
 *   category           - R2 upload category ('league-logo', 'league-banner', 'sponsor-logo', 'photo')
 *   currentImageUrl    - existing image URL to preview (controlled by parent)
 *   label              - optional label string
 *   hint               - optional hint string (e.g. max size)
 *   accept             - optional MIME accept string (default: image/*)
 */
function ImageUploader({ onUpload, onRemove, category, currentImageUrl, label, hint, accept = 'image/*' }) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const result = await api.uploadImage(file, category);
      onUpload(result.url, result.key);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    handleFile(e.target.files[0]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const zone = {
    border: `2px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
    borderRadius: 8,
    padding: '20px',
    textAlign: 'center',
    background: dragging ? '#eff6ff' : '#f9fafb',
    cursor: uploading ? 'default' : 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  };

  return (
    <div>
      {label && <label className="lwp-label">{label}</label>}
      {currentImageUrl ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 6 }}>
          <img
            src={currentImageUrl}
            alt="preview"
            style={{ maxHeight: 80, maxWidth: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              type="button"
              className="lwp-btn-secondary"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              style={{ fontSize: '0.8rem', padding: '4px 10px' }}
            >
              {uploading ? 'Uploading...' : 'Replace'}
            </button>
            <button
              type="button"
              className="lwp-btn-danger-ghost"
              onClick={onRemove}
              disabled={uploading}
              style={{ fontSize: '0.8rem', padding: '4px 10px' }}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          style={zone}
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {uploading ? (
            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>Uploading...</span>
          ) : (
            <>
              <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>🖼</div>
              <div style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
                Click or drag to upload
              </div>
              {hint && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 2 }}>{hint}</div>}
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {error && <div className="lwp-subdomain-err" style={{ marginTop: 4 }}>{error}</div>}
    </div>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type === 'error' ? '#dc2626' : '#16a34a';
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: bg, color: '#fff', padding: '10px 20px', borderRadius: 8,
      fontWeight: 600, fontSize: '0.9rem', zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    }}>
      {toast.message}
    </div>
  );
}

// ─── Theme Preview ────────────────────────────────────────────────────────────

function ThemePreview({ theme, leagueName }) {
  const hdr = {
    background: theme.primaryColor,
    color: '#fff',
    padding: '16px 20px',
    borderRadius: '8px 8px 0 0',
    display: 'flex', alignItems: 'center', gap: 12,
  };
  const nav = {
    background: theme.secondaryColor,
    padding: '8px 20px',
    display: 'flex', gap: 16,
    borderRadius: '0 0 8px 8px',
  };
  const navItem = { color: '#fff', fontSize: '0.8rem', opacity: 0.9 };
  const body = {
    background: theme.backgroundColor,
    color: theme.textColor,
    padding: '12px 20px',
    borderRadius: 4,
    marginTop: 4,
    fontSize: '0.85rem',
    minHeight: 60,
    border: `1px solid ${theme.secondaryColor}33`,
  };

  return (
    <div className="lwp-preview-wrap">
      <div style={hdr}>
        {theme.logoUrl && (
          <img
            src={theme.logoUrl}
            alt="logo"
            style={{ height: 32, width: 32, objectFit: 'contain', borderRadius: 4 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <span style={{ fontWeight: 700, fontSize: '1rem' }}>{leagueName || 'My League'}</span>
      </div>
      <div style={nav}>
        {['Schedule', 'Standings', 'News'].map(s => (
          <span key={s} style={navItem}>{s}</span>
        ))}
      </div>
      <div style={body}>
        <p style={{ margin: 0 }}>League content appears here...</p>
      </div>
    </div>
  );
}

// ─── Announcement Modal ───────────────────────────────────────────────────────

function AnnouncementModal({ leagueId, existing, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    title: existing?.title || '',
    content: existing?.content || '',
    pinned: existing?.pinned ?? false,
    publishedAt: existing?.publishedAt
      ? existing.publishedAt.slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.content.trim()) { setError('Content is required'); return; }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        pinned: form.pinned,
        publishedAt: form.publishedAt,
      };
      if (existing) {
        await api.updateAnnouncement(leagueId, existing.id, payload);
      } else {
        await api.createAnnouncement(leagueId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lwp-modal-overlay" onClick={onClose}>
      <div className="lwp-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="lwp-modal-title">{existing ? 'Edit Announcement' : 'New Announcement'}</h3>
        {error && <div className="lwp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="lwp-field">
            <label className="lwp-label">Title</label>
            <input
              className="lwp-input"
              value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Announcement title"
              maxLength={200}
            />
          </div>
          <div className="lwp-field">
            <label className="lwp-label">Content</label>
            <textarea
              className="lwp-textarea"
              rows={4}
              value={form.content}
              onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Announcement content..."
            />
          </div>
          <div className="lwp-field-row">
            <div className="lwp-field" style={{ flex: 1 }}>
              <label className="lwp-label">Date</label>
              <input
                type="date"
                className="lwp-input"
                value={form.publishedAt}
                onChange={(e) => setForm(f => ({ ...f, publishedAt: e.target.value }))}
              />
            </div>
            <div className="lwp-checkbox-field" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>
              <label className="lwp-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.pinned}
                  onChange={(e) => setForm(f => ({ ...f, pinned: e.target.checked }))}
                />
                <span>Pin to top</span>
              </label>
            </div>
          </div>
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="lwp-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (existing ? 'Save Changes' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Sponsor Modal ────────────────────────────────────────────────────────────

function SponsorModal({ leagueId, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: existing?.name || '',
    tier: existing?.tier || 'bronze',
    logoUrl: existing?.logoUrl || '',
    websiteUrl: existing?.websiteUrl || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        tier: form.tier,
        logoUrl: form.logoUrl.trim() || null,
        websiteUrl: form.websiteUrl.trim() || null,
      };
      if (existing) {
        await api.updateSponsor(leagueId, existing.id, payload);
      } else {
        await api.createSponsor(leagueId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lwp-modal-overlay" onClick={onClose}>
      <div className="lwp-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="lwp-modal-title">{existing ? 'Edit Sponsor' : 'Add Sponsor'}</h3>
        {error && <div className="lwp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="lwp-field">
            <label className="lwp-label">Sponsor Name</label>
            <input
              className="lwp-input"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Sponsor name"
              maxLength={200}
            />
          </div>
          <div className="lwp-field">
            <label className="lwp-label">Tier</label>
            <select
              className="lwp-select"
              value={form.tier}
              onChange={(e) => setForm(f => ({ ...f, tier: e.target.value }))}
            >
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="bronze">Bronze</option>
            </select>
          </div>
          <div className="lwp-field">
            <ImageUploader
              label={<>Logo <span className="lwp-optional">(optional)</span></>}
              hint="PNG, JPG, WebP · max 5 MB"
              category="sponsor-logo"
              currentImageUrl={form.logoUrl || null}
              onUpload={(url) => setForm(f => ({ ...f, logoUrl: url }))}
              onRemove={() => setForm(f => ({ ...f, logoUrl: '' }))}
            />
          </div>
          <div className="lwp-field">
            <label className="lwp-label">Website URL <span className="lwp-optional">(optional)</span></label>
            <input
              className="lwp-input"
              value={form.websiteUrl}
              onChange={(e) => setForm(f => ({ ...f, websiteUrl: e.target.value }))}
              placeholder="https://sponsor.com"
            />
          </div>
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="lwp-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (existing ? 'Save Changes' : 'Add Sponsor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LeagueWebsitePage({ leagueId, leagueName, onBack }) {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Website config
  const [enabled, setEnabled] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [aboutContent, setAboutContent] = useState('');

  // Subdomain
  const [subdomain, setSubdomain] = useState('');             // saved value from server
  const [subdomainInput, setSubdomainInput] = useState('');   // current input value
  const [subdomainAvail, setSubdomainAvail] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [subdomainAvailMsg, setSubdomainAvailMsg] = useState('');
  const [subdomainSaving, setSubdomainSaving] = useState(false);
  const [subdomainError, setSubdomainError] = useState('');
  const [subdomainSuccess, setSubdomainSuccess] = useState('');
  const [confirmRelease, setConfirmRelease] = useState(false);
  const subdomainTimer = useRef(null);

  // Custom Domain
  const [customDomain, setCustomDomain] = useState('');
  const [customDomainStatus, setCustomDomainStatus] = useState(null);
  const [customDomainDnsRecords, setCustomDomainDnsRecords] = useState([]);
  const [customDomainInput, setCustomDomainInput] = useState('');
  const [customDomainSaving, setCustomDomainSaving] = useState(false);
  const [customDomainChecking, setCustomDomainChecking] = useState(false);
  const [customDomainError, setCustomDomainError] = useState('');
  const [customDomainSuccess, setCustomDomainSuccess] = useState('');
  const [confirmDeleteCustomDomain, setConfirmDeleteCustomDomain] = useState(false);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [announcementModal, setAnnouncementModal] = useState(null); // null | 'new' | announcement obj

  // Sponsors
  const [sponsors, setSponsors] = useState([]);
  const [sponsorModal, setSponsorModal] = useState(null); // null | 'new' | sponsor obj

  // Photos
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [editingCaptionId, setEditingCaptionId] = useState(null);
  const [editingCaptionText, setEditingCaptionText] = useState('');
  const [confirmDeletePhoto, setConfirmDeletePhoto] = useState(null);

  // Delete confirmations
  const [confirmDeleteAnn, setConfirmDeleteAnn] = useState(null);
  const [confirmDeleteSpon, setConfirmDeleteSpon] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load data ──────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wsRes, annRes, sponRes, photoRes] = await Promise.all([
        api.getWebsite(leagueId),
        api.getAnnouncements(leagueId),
        api.getSponsors(leagueId),
        api.getPhotos(leagueId),
      ]);

      if (wsRes.website) {
        const ws = wsRes.website;
        setEnabled(ws.enabled);
        setTheme({ ...DEFAULT_THEME, ...ws.theme });
        setSections({ ...DEFAULT_SECTIONS, ...ws.sections });
        setAboutContent(ws.aboutContent || '');
        setSubdomain(ws.subdomain || '');
        setSubdomainInput(ws.subdomain || '');
        setCustomDomain(ws.customDomain || '');
        setCustomDomainStatus(ws.customDomainStatus || null);
        if (ws.customDomain) {
          api.getCustomDomainStatus(leagueId).then(statusRes => {
            setCustomDomainStatus(statusRes.status);
            setCustomDomainDnsRecords(statusRes.dnsRecords || []);
          }).catch(() => {});
        }
      }

      setAnnouncements(annRes.announcements || []);
      setSponsors(sponRes.sponsors || []);
      setPhotos(photoRes.photos || []);
    } catch (err) {
      showToast('Failed to load website config');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Save helpers ───────────────────────────────────────────────────────────

  const saveWebsite = async (updates) => {
    setSaving(true);
    try {
      await api.updateWebsite(leagueId, updates);
      return true;
    } catch (err) {
      showToast(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    const ok = await saveWebsite({ enabled: next });
    if (!ok) setEnabled(!next); // revert
    else showToast(next ? 'Website enabled' : 'Website disabled', 'success');
  };

  const handleSaveTheme = async () => {
    const ok = await saveWebsite({ theme });
    if (ok) showToast('Theme saved', 'success');
  };

  const handleSaveAbout = async () => {
    const ok = await saveWebsite({ aboutContent });
    if (ok) showToast('About section saved', 'success');
  };

  const handleSaveSections = async () => {
    const ok = await saveWebsite({ sections });
    if (ok) showToast('Sections saved', 'success');
  };

  // ── Subdomain ──────────────────────────────────────────────────────────────

  const handleSubdomainInputChange = (val) => {
    const normalized = val.trim().toLowerCase();
    setSubdomainInput(val);
    setSubdomainError('');
    setSubdomainSuccess('');
    clearTimeout(subdomainTimer.current);

    if (!normalized || normalized === subdomain) {
      setSubdomainAvail(null);
      setSubdomainAvailMsg('');
      return;
    }

    setSubdomainAvail('checking');
    setSubdomainAvailMsg('');
    subdomainTimer.current = setTimeout(async () => {
      try {
        const res = await api.checkSubdomainAvailability(normalized);
        if (res.error) {
          setSubdomainAvail('invalid');
          setSubdomainAvailMsg(res.error);
        } else if (res.available) {
          setSubdomainAvail('available');
          setSubdomainAvailMsg('Available!');
        } else {
          setSubdomainAvail('taken');
          setSubdomainAvailMsg('Already taken');
        }
      } catch {
        setSubdomainAvail(null);
        setSubdomainAvailMsg('');
      }
    }, 500);
  };

  const handleSubdomainSave = async () => {
    const val = subdomainInput.trim().toLowerCase();
    if (!val || val === subdomain) return;
    setSubdomainSaving(true);
    setSubdomainError('');
    setSubdomainSuccess('');
    try {
      let res;
      if (subdomain) {
        res = await api.changeSubdomain(leagueId, val);
      } else {
        res = await api.claimSubdomain(leagueId, val);
      }
      const saved = res.website?.subdomain || res.subdomain || val;
      setSubdomain(saved);
      setSubdomainInput(saved);
      setSubdomainAvail(null);
      setSubdomainAvailMsg('');
      setSubdomainSuccess(subdomain ? 'Subdomain updated!' : 'Subdomain claimed!');
      showToast(subdomain ? 'Subdomain updated' : 'Subdomain claimed', 'success');
    } catch (err) {
      setSubdomainError(err.message);
    } finally {
      setSubdomainSaving(false);
    }
  };

  const handleSubdomainRelease = async () => {
    setSubdomainSaving(true);
    setSubdomainError('');
    setSubdomainSuccess('');
    try {
      await api.releaseSubdomain(leagueId);
      setSubdomain('');
      setSubdomainInput('');
      setSubdomainAvail(null);
      setSubdomainAvailMsg('');
      setConfirmRelease(false);
      showToast('Subdomain released', 'success');
    } catch (err) {
      setSubdomainError(err.message);
      setConfirmRelease(false);
    } finally {
      setSubdomainSaving(false);
    }
  };

  // ── Custom Domain ──────────────────────────────────────────────────────────

  const handleCustomDomainSetup = async () => {
    const val = customDomainInput.trim().toLowerCase();
    if (!val) return;
    setCustomDomainSaving(true);
    setCustomDomainError('');
    setCustomDomainSuccess('');
    try {
      const res = await api.setupCustomDomain(leagueId, val);
      setCustomDomain(res.domain);
      setCustomDomainStatus(res.status);
      setCustomDomainDnsRecords(res.dnsRecords || []);
      showToast('Custom domain connected — add the DNS records below', 'success');
    } catch (err) {
      setCustomDomainError(err.message);
    } finally {
      setCustomDomainSaving(false);
    }
  };

  const handleCheckCustomDomainStatus = async () => {
    setCustomDomainChecking(true);
    setCustomDomainError('');
    try {
      const res = await api.getCustomDomainStatus(leagueId);
      setCustomDomainStatus(res.status);
      setCustomDomainDnsRecords(res.dnsRecords || []);
      if (res.status === 'active') {
        setCustomDomainSuccess('Domain verified and active!');
        showToast('Custom domain is now active!', 'success');
      } else if (res.status === 'failed') {
        setCustomDomainError('Verification failed. Check your DNS records and try again.');
      } else {
        showToast('DNS not yet detected — propagation can take up to 24 hours', 'error');
      }
    } catch (err) {
      setCustomDomainError(err.message);
    } finally {
      setCustomDomainChecking(false);
    }
  };

  const handleDeleteCustomDomain = async () => {
    setCustomDomainSaving(true);
    setCustomDomainError('');
    try {
      await api.deleteCustomDomain(leagueId);
      setCustomDomain('');
      setCustomDomainStatus(null);
      setCustomDomainDnsRecords([]);
      setCustomDomainInput('');
      setCustomDomainSuccess('');
      setConfirmDeleteCustomDomain(false);
      showToast('Custom domain removed', 'success');
    } catch (err) {
      setCustomDomainError(err.message);
      setConfirmDeleteCustomDomain(false);
    } finally {
      setCustomDomainSaving(false);
    }
  };

  // ── Sections reorder ───────────────────────────────────────────────────────

  const moveSection = (key, dir) => {
    const sorted = Object.entries(sections).sort((a, b) => a[1].order - b[1].order);
    const idx = sorted.findIndex(([k]) => k === key);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const newSections = { ...sections };
    const tempOrder = newSections[sorted[idx][0]].order;
    newSections[sorted[idx][0]] = { ...newSections[sorted[idx][0]], order: newSections[sorted[swapIdx][0]].order };
    newSections[sorted[swapIdx][0]] = { ...newSections[sorted[swapIdx][0]], order: tempOrder };
    setSections(newSections);
  };

  const toggleSection = (key) => {
    setSections(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };

  // ── Announcements ──────────────────────────────────────────────────────────

  const handleDeleteAnnouncement = async () => {
    if (!confirmDeleteAnn) return;
    setDeleting(true);
    try {
      await api.deleteAnnouncement(leagueId, confirmDeleteAnn.id);
      setAnnouncements(prev => prev.filter(a => a.id !== confirmDeleteAnn.id));
      setConfirmDeleteAnn(null);
      showToast('Announcement deleted', 'success');
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const reloadAnnouncements = async () => {
    try {
      const res = await api.getAnnouncements(leagueId);
      setAnnouncements(res.announcements || []);
    } catch {}
  };

  // ── Sponsors ───────────────────────────────────────────────────────────────

  const handleDeleteSponsor = async () => {
    if (!confirmDeleteSpon) return;
    setDeleting(true);
    try {
      await api.deleteSponsor(leagueId, confirmDeleteSpon.id);
      setSponsors(prev => prev.filter(s => s.id !== confirmDeleteSpon.id));
      setConfirmDeleteSpon(null);
      showToast('Sponsor deleted', 'success');
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const reloadSponsors = async () => {
    try {
      const res = await api.getSponsors(leagueId);
      setSponsors(res.sponsors || []);
    } catch {}
  };

  // ── Photos ─────────────────────────────────────────────────────────────────

  const handlePhotoUpload = async (url, key) => {
    setPhotoUploading(true);
    try {
      const res = await api.addPhoto(leagueId, {
        imageUrl: url,
        imageKey: key,
        caption: photoCaption.trim() || null,
      });
      setPhotos(prev => [...prev, res.photo]);
      setPhotoCaption('');
    } catch (err) {
      showToast(err.message);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSaveCaption = async (photo) => {
    try {
      await api.updatePhoto(leagueId, photo.id, { caption: editingCaptionText.trim() || null });
      setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, caption: editingCaptionText.trim() || null } : p));
      setEditingCaptionId(null);
    } catch (err) {
      showToast(err.message);
    }
  };

  const handleDeletePhoto = async () => {
    if (!confirmDeletePhoto) return;
    setDeleting(true);
    try {
      await api.deletePhoto(leagueId, confirmDeletePhoto.id);
      setPhotos(prev => prev.filter(p => p.id !== confirmDeletePhoto.id));
      setConfirmDeletePhoto(null);
      showToast('Photo deleted', 'success');
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Public URL ─────────────────────────────────────────────────────────────

  const publicUrl = subdomain ? `https://${subdomain}.leaguemaker.app` : null;

  // ── Render ─────────────────────────────────────────────────────────────────

  const sortedSections = Object.entries(sections).sort((a, b) => a[1].order - b[1].order);

  if (loading) {
    return (
      <div className="lwp-page">
        <div className="lwp-loading">Loading website config...</div>
      </div>
    );
  }

  if (userPlan !== 'pro') {
    return (
      <div className="lwp-page">
        <div className="lwp-header">
          <button className="lwp-back-btn" onClick={onBack}>← Back</button>
          <div className="lwp-header-title">
            <h2 className="lwp-page-title">Website</h2>
            <span className="lwp-league-name">{leagueName}</span>
          </div>
        </div>
        <UpgradePrompt
          requiredPlan="pro"
          feature="League Website"
        />
      </div>
    );
  }

  return (
    <div className="lwp-page">
      <Toast toast={toast} />

      {/* ── Header ── */}
      <div className="lwp-header">
        <button className="lwp-back-btn" onClick={onBack}>← Back</button>
        <div className="lwp-header-title">
          <h2 className="lwp-page-title">Website</h2>
          <span className="lwp-league-name">{leagueName}</span>
        </div>
        {publicUrl && enabled && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="lwp-view-btn"
          >
            View Website ↗
          </a>
        )}
      </div>

      {/* ── Website Setup ── */}
      <section className="lwp-section">
        <h3 className="lwp-section-title">Website Setup</h3>

        <div className="lwp-enable-row">
          <div>
            <div className="lwp-label">Enable Public Website</div>
            <div className="lwp-hint">
              {enabled
                ? (publicUrl ? `Live at ${publicUrl}` : 'Set a subdomain to make it accessible')
                : 'Your website is currently hidden from the public.'}
            </div>
          </div>
          <button
            className={`lwp-toggle ${enabled ? 'lwp-toggle-on' : ''}`}
            onClick={handleToggleEnabled}
            disabled={saving}
          >
            {enabled ? 'On' : 'Off'}
          </button>
        </div>

        <div className="lwp-field">
          <label className="lwp-label">Subdomain</label>

          {subdomain && !confirmRelease && (
            <div className="lwp-subdomain-current">
              <span className="lwp-subdomain-live">
                <span className="lwp-subdomain-dot" />
                <a href={`https://${subdomain}.leaguemaker.app`} target="_blank" rel="noopener noreferrer">
                  {subdomain}.leaguemaker.app
                </a>
              </span>
              <button
                className="lwp-btn-ghost lwp-subdomain-release-btn"
                onClick={() => setConfirmRelease(true)}
                disabled={subdomainSaving}
              >
                Release
              </button>
            </div>
          )}

          {confirmRelease && (
            <div className="lwp-subdomain-release-confirm">
              <span>Release <strong>{subdomain}.leaguemaker.app</strong>? This frees it for others.</span>
              <div className="lwp-subdomain-release-actions">
                <button className="lwp-btn-danger" onClick={handleSubdomainRelease} disabled={subdomainSaving}>
                  {subdomainSaving ? 'Releasing...' : 'Yes, Release'}
                </button>
                <button className="lwp-btn-ghost" onClick={() => setConfirmRelease(false)} disabled={subdomainSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!confirmRelease && (
            <>
              <div className="lwp-subdomain-row">
                <input
                  className="lwp-input lwp-subdomain-input"
                  value={subdomainInput}
                  onChange={(e) => handleSubdomainInputChange(e.target.value)}
                  placeholder="my-league"
                  maxLength={63}
                  disabled={subdomainSaving}
                />
                <span className="lwp-subdomain-suffix">.leaguemaker.app</span>
                {subdomainAvail === 'checking' && (
                  <span className="lwp-subdomain-avail lwp-subdomain-checking">Checking…</span>
                )}
                {subdomainAvail === 'available' && (
                  <span className="lwp-subdomain-avail lwp-subdomain-avail-ok">✓ Available</span>
                )}
                {(subdomainAvail === 'taken' || subdomainAvail === 'invalid') && (
                  <span className="lwp-subdomain-avail lwp-subdomain-avail-no">✗ {subdomainAvailMsg}</span>
                )}
                <button
                  className="lwp-btn-primary"
                  onClick={handleSubdomainSave}
                  disabled={
                    subdomainSaving ||
                    !subdomainInput.trim() ||
                    subdomainInput.trim().toLowerCase() === subdomain ||
                    subdomainAvail === 'checking' ||
                    subdomainAvail === 'taken' ||
                    subdomainAvail === 'invalid'
                  }
                >
                  {subdomainSaving ? 'Saving…' : subdomain ? 'Update' : 'Claim'}
                </button>
              </div>
              <div className="lwp-hint">Lowercase letters, numbers, and hyphens only. 3–63 characters. Max 3 changes per day.</div>
            </>
          )}

          {subdomainSuccess && <div className="lwp-subdomain-ok">{subdomainSuccess}</div>}
          {subdomainError && <div className="lwp-subdomain-err">{subdomainError}</div>}
        </div>

        {/* ── Custom Domain ── */}
        <div className="lwp-field">
          <label className="lwp-label">Custom Domain</label>

          {/* Step 1: No custom domain set — input form */}
          {!customDomain && (
            <>
              <div className="lwp-subdomain-row">
                <input
                  className="lwp-input"
                  value={customDomainInput}
                  onChange={(e) => { setCustomDomainInput(e.target.value); setCustomDomainError(''); }}
                  placeholder="yourleague.com"
                  disabled={customDomainSaving}
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomDomainSetup()}
                />
                <button
                  className="lwp-btn-primary"
                  onClick={handleCustomDomainSetup}
                  disabled={customDomainSaving || !customDomainInput.trim()}
                >
                  {customDomainSaving ? 'Setting up…' : 'Connect'}
                </button>
              </div>
              <div className="lwp-hint">Connect your own domain (e.g. springfieldsoftball.com). You'll add DNS records at your registrar after connecting.</div>
            </>
          )}

          {/* Step 2: Pending verification or failed — show DNS instructions */}
          {customDomain && customDomainStatus !== 'active' && !confirmDeleteCustomDomain && (
            <div className="lwp-custom-domain-setup">
              <div className="lwp-custom-domain-header">
                <span className="lwp-custom-domain-name">{customDomain}</span>
                <span className={`lwp-custom-domain-badge lwp-badge-${customDomainStatus}`}>
                  {customDomainStatus === 'failed' ? 'Failed' : 'Pending Verification'}
                </span>
              </div>

              {customDomainDnsRecords.length > 0 && (
                <div className="lwp-dns-instructions">
                  <p className="lwp-dns-intro">Add these DNS records at your domain registrar, then click <strong>Check DNS Status</strong>:</p>
                  <div className="lwp-dns-table">
                    <div className="lwp-dns-header-row">
                      <span>Type</span>
                      <span>Name</span>
                      <span>Value / Target</span>
                    </div>
                    {customDomainDnsRecords.map((rec, i) => (
                      <div key={i} className="lwp-dns-row">
                        <span className="lwp-dns-type-badge">{rec.type}</span>
                        <span className="lwp-mono lwp-dns-cell">{rec.name}</span>
                        <span className="lwp-mono lwp-dns-cell">{rec.target || rec.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="lwp-hint lwp-dns-note">DNS changes can take up to 24 hours to propagate globally.</div>
                </div>
              )}

              <div className="lwp-custom-domain-actions">
                <button
                  className="lwp-btn-primary"
                  onClick={handleCheckCustomDomainStatus}
                  disabled={customDomainChecking || customDomainSaving}
                >
                  {customDomainChecking ? 'Checking…' : 'Check DNS Status'}
                </button>
                <button
                  className="lwp-btn-ghost"
                  onClick={() => setConfirmDeleteCustomDomain(true)}
                  disabled={customDomainSaving || customDomainChecking}
                >
                  Remove Domain
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Active — show live link */}
          {customDomain && customDomainStatus === 'active' && !confirmDeleteCustomDomain && (
            <div className="lwp-subdomain-current">
              <span className="lwp-subdomain-live">
                <span className="lwp-subdomain-dot" />
                <a href={`https://${customDomain}`} target="_blank" rel="noopener noreferrer">
                  {customDomain}
                </a>
              </span>
              <button
                className="lwp-btn-ghost lwp-subdomain-release-btn"
                onClick={() => setConfirmDeleteCustomDomain(true)}
                disabled={customDomainSaving}
              >
                Remove
              </button>
            </div>
          )}

          {/* Confirm remove */}
          {confirmDeleteCustomDomain && (
            <div className="lwp-subdomain-release-confirm">
              <span>Remove <strong>{customDomain}</strong>? This disconnects your custom domain.</span>
              <div className="lwp-subdomain-release-actions">
                <button className="lwp-btn-danger" onClick={handleDeleteCustomDomain} disabled={customDomainSaving}>
                  {customDomainSaving ? 'Removing…' : 'Yes, Remove'}
                </button>
                <button className="lwp-btn-ghost" onClick={() => setConfirmDeleteCustomDomain(false)} disabled={customDomainSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {customDomainSuccess && <div className="lwp-subdomain-ok">{customDomainSuccess}</div>}
          {customDomainError && <div className="lwp-subdomain-err">{customDomainError}</div>}
        </div>
      </section>

      {/* ── Theme Editor ── */}
      <section className="lwp-section">
        <h3 className="lwp-section-title">Theme</h3>
        <div className="lwp-theme-layout">
          <div className="lwp-theme-controls">
            {[
              { key: 'primaryColor',    label: 'Primary Color' },
              { key: 'secondaryColor',  label: 'Secondary Color' },
              { key: 'backgroundColor', label: 'Background Color' },
              { key: 'textColor',       label: 'Text Color' },
            ].map(({ key, label }) => (
              <div key={key} className="lwp-color-field">
                <label className="lwp-label">{label}</label>
                <div className="lwp-color-row">
                  <input
                    type="color"
                    className="lwp-color-swatch"
                    value={theme[key]}
                    onChange={(e) => setTheme(t => ({ ...t, [key]: e.target.value }))}
                  />
                  <input
                    className="lwp-input lwp-color-hex"
                    value={theme[key]}
                    onChange={(e) => setTheme(t => ({ ...t, [key]: e.target.value }))}
                    maxLength={7}
                  />
                </div>
              </div>
            ))}
            <div className="lwp-field">
              <ImageUploader
                label="Logo"
                hint="PNG, JPG, WebP · max 5 MB"
                category="league-logo"
                currentImageUrl={theme.logoUrl}
                onUpload={(url) => setTheme(t => ({ ...t, logoUrl: url }))}
                onRemove={() => setTheme(t => ({ ...t, logoUrl: null }))}
              />
            </div>
            <div className="lwp-field">
              <ImageUploader
                label="Banner"
                hint="PNG, JPG, WebP · max 10 MB · wide format recommended"
                category="league-banner"
                currentImageUrl={theme.bannerUrl}
                onUpload={(url) => setTheme(t => ({ ...t, bannerUrl: url }))}
                onRemove={() => setTheme(t => ({ ...t, bannerUrl: null }))}
              />
            </div>
            <button className="lwp-btn-primary lwp-save-btn" onClick={handleSaveTheme} disabled={saving}>
              {saving ? 'Saving...' : 'Save Theme'}
            </button>
          </div>

          <div className="lwp-theme-preview-col">
            <div className="lwp-label" style={{ marginBottom: 8 }}>Live Preview</div>
            <ThemePreview theme={theme} leagueName={leagueName} />
          </div>
        </div>
      </section>

      {/* ── Sections Manager ── */}
      <section className="lwp-section">
        <h3 className="lwp-section-title">Sections</h3>
        <div className="lwp-sections-list">
          {sortedSections.map(([key, cfg], idx) => (
            <div key={key} className="lwp-section-row">
              <div className="lwp-section-reorder">
                <button
                  className="lwp-reorder-btn"
                  onClick={() => moveSection(key, -1)}
                  disabled={idx === 0}
                  title="Move up"
                >▲</button>
                <button
                  className="lwp-reorder-btn"
                  onClick={() => moveSection(key, 1)}
                  disabled={idx === sortedSections.length - 1}
                  title="Move down"
                >▼</button>
              </div>
              <span className="lwp-section-name">{SECTION_LABELS[key]}</span>
              <label className="lwp-toggle-label">
                <input
                  type="checkbox"
                  checked={cfg.enabled}
                  onChange={() => toggleSection(key)}
                  className="lwp-toggle-checkbox"
                />
                <span className={`lwp-toggle-pill ${cfg.enabled ? 'on' : ''}`}>
                  {cfg.enabled ? 'On' : 'Off'}
                </span>
              </label>
            </div>
          ))}
        </div>
        <button className="lwp-btn-primary lwp-save-btn" onClick={handleSaveSections} disabled={saving}>
          {saving ? 'Saving...' : 'Save Sections'}
        </button>
      </section>

      {/* ── About Editor ── */}
      <section className="lwp-section">
        <h3 className="lwp-section-title">About Section</h3>
        <div className="lwp-about-layout">
          <div className="lwp-field" style={{ flex: 1 }}>
            <label className="lwp-label">Content</label>
            <textarea
              className="lwp-textarea lwp-about-textarea"
              rows={8}
              value={aboutContent}
              onChange={(e) => setAboutContent(e.target.value)}
              placeholder="Tell people about your league..."
            />
            <button className="lwp-btn-primary lwp-save-btn" onClick={handleSaveAbout} disabled={saving}>
              {saving ? 'Saving...' : 'Save About'}
            </button>
          </div>
          {aboutContent && (
            <div className="lwp-about-preview">
              <div className="lwp-label" style={{ marginBottom: 8 }}>Preview</div>
              <div className="lwp-about-preview-body">
                {aboutContent.split('\n').map((line, i) => (
                  <p key={i} style={{ margin: '0 0 8px' }}>{line}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Announcements ── */}
      <section className="lwp-section">
        <div className="lwp-section-header">
          <h3 className="lwp-section-title" style={{ margin: 0 }}>Announcements</h3>
          <button className="lwp-btn-primary" onClick={() => setAnnouncementModal('new')}>
            + New Announcement
          </button>
        </div>
        {announcements.length === 0 ? (
          <div className="lwp-empty">No announcements yet.</div>
        ) : (
          <div className="lwp-list">
            {announcements.map(ann => (
              <div key={ann.id} className="lwp-list-row">
                <div className="lwp-list-main">
                  <div className="lwp-list-title">
                    {ann.title}
                    {ann.pinned && <span className="lwp-pinned-badge">Pinned</span>}
                  </div>
                  <div className="lwp-list-sub">
                    {ann.publishedAt ? new Date(ann.publishedAt).toLocaleDateString() : ''}
                  </div>
                </div>
                <div className="lwp-list-actions">
                  <button className="lwp-btn-ghost" onClick={() => setAnnouncementModal(ann)}>Edit</button>
                  <button className="lwp-btn-danger-ghost" onClick={() => setConfirmDeleteAnn(ann)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Sponsors ── */}
      <section className="lwp-section">
        <div className="lwp-section-header">
          <h3 className="lwp-section-title" style={{ margin: 0 }}>Sponsors</h3>
          <button className="lwp-btn-primary" onClick={() => setSponsorModal('new')}>
            + Add Sponsor
          </button>
        </div>
        {sponsors.length === 0 ? (
          <div className="lwp-empty">No sponsors yet.</div>
        ) : (
          <div className="lwp-list">
            {sponsors.map(spon => (
              <div key={spon.id} className="lwp-list-row">
                <div className="lwp-list-main">
                  <div className="lwp-list-title">
                    {spon.logoUrl && (
                      <img
                        src={spon.logoUrl}
                        alt="logo"
                        className="lwp-sponsor-logo"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {spon.name}
                    <span
                      className="lwp-tier-badge"
                      style={{ background: TIER_COLORS[spon.tier] }}
                    >
                      {TIER_LABELS[spon.tier]}
                    </span>
                  </div>
                  {spon.websiteUrl && (
                    <div className="lwp-list-sub">{spon.websiteUrl}</div>
                  )}
                </div>
                <div className="lwp-list-actions">
                  <button className="lwp-btn-ghost" onClick={() => setSponsorModal(spon)}>Edit</button>
                  <button className="lwp-btn-danger-ghost" onClick={() => setConfirmDeleteSpon(spon)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Photos ── */}
      <section className="lwp-section">
        <h3 className="lwp-section-title">Photo Gallery</h3>
        <div style={{ marginBottom: 20 }}>
          <div className="lwp-field" style={{ marginBottom: 8 }}>
            <label className="lwp-label">Caption (optional — add before uploading)</label>
            <input
              className="lwp-input"
              value={photoCaption}
              onChange={(e) => setPhotoCaption(e.target.value)}
              placeholder="e.g. Championship night!"
              maxLength={500}
              disabled={photoUploading}
            />
          </div>
          <ImageUploader
            category="photo"
            hint="JPG, PNG, WebP · max 10 MB"
            currentImageUrl={null}
            onUpload={handlePhotoUpload}
            onRemove={() => {}}
          />
        </div>

        {photos.length === 0 ? (
          <div className="lwp-empty">No photos yet. Upload the first one above.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {photos.map((photo) => (
              <div key={photo.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#f9fafb',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <img
                  src={photo.imageUrl}
                  alt={photo.caption || 'League photo'}
                  style={{ width: '100%', height: 120, objectFit: 'cover' }}
                  onError={(e) => { e.target.style.background = '#e5e7eb'; e.target.style.display = 'block'; }}
                />
                <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {editingCaptionId === photo.id ? (
                    <>
                      <input
                        className="lwp-input"
                        value={editingCaptionText}
                        onChange={(e) => setEditingCaptionText(e.target.value)}
                        placeholder="Caption..."
                        maxLength={500}
                        autoFocus
                        style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                      />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="lwp-btn-primary"
                          style={{ fontSize: '0.75rem', padding: '3px 8px', flex: 1 }}
                          onClick={() => handleSaveCaption(photo)}
                        >
                          Save
                        </button>
                        <button
                          className="lwp-btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '3px 8px' }}
                          onClick={() => setEditingCaptionId(null)}
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '0.78rem', color: '#6b7280', flex: 1, fontStyle: photo.caption ? 'normal' : 'italic' }}>
                        {photo.caption || 'No caption'}
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="lwp-btn-ghost"
                          style={{ fontSize: '0.75rem', padding: '3px 8px', flex: 1 }}
                          onClick={() => { setEditingCaptionId(photo.id); setEditingCaptionText(photo.caption || ''); }}
                        >
                          Edit
                        </button>
                        <button
                          className="lwp-btn-danger-ghost"
                          style={{ fontSize: '0.75rem', padding: '3px 8px' }}
                          onClick={() => setConfirmDeletePhoto(photo)}
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Modals ── */}

      {announcementModal && (
        <AnnouncementModal
          leagueId={leagueId}
          existing={announcementModal === 'new' ? null : announcementModal}
          onClose={() => setAnnouncementModal(null)}
          onSaved={async () => {
            setAnnouncementModal(null);
            await reloadAnnouncements();
            showToast('Announcement saved', 'success');
          }}
          showToast={showToast}
        />
      )}

      {sponsorModal && (
        <SponsorModal
          leagueId={leagueId}
          existing={sponsorModal === 'new' ? null : sponsorModal}
          onClose={() => setSponsorModal(null)}
          onSaved={async () => {
            setSponsorModal(null);
            await reloadSponsors();
            showToast('Sponsor saved', 'success');
          }}
        />
      )}

      {/* Delete announcement confirm */}
      {confirmDeleteAnn && (
        <div className="lwp-modal-overlay" onClick={() => setConfirmDeleteAnn(null)}>
          <div className="lwp-modal lwp-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="lwp-modal-title">Delete Announcement?</h3>
            <p className="lwp-confirm-text">
              Are you sure you want to delete <strong>"{confirmDeleteAnn.title}"</strong>? This cannot be undone.
            </p>
            <div className="lwp-modal-actions">
              <button className="lwp-btn-secondary" onClick={() => setConfirmDeleteAnn(null)} disabled={deleting}>Cancel</button>
              <button className="lwp-btn-danger" onClick={handleDeleteAnnouncement} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete photo confirm */}
      {confirmDeletePhoto && (
        <div className="lwp-modal-overlay" onClick={() => setConfirmDeletePhoto(null)}>
          <div className="lwp-modal lwp-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="lwp-modal-title">Delete Photo?</h3>
            <p className="lwp-confirm-text">This will permanently delete the photo. This cannot be undone.</p>
            <div className="lwp-modal-actions">
              <button className="lwp-btn-secondary" onClick={() => setConfirmDeletePhoto(null)} disabled={deleting}>Cancel</button>
              <button className="lwp-btn-danger" onClick={handleDeletePhoto} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete sponsor confirm */}
      {confirmDeleteSpon && (
        <div className="lwp-modal-overlay" onClick={() => setConfirmDeleteSpon(null)}>
          <div className="lwp-modal lwp-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="lwp-modal-title">Delete Sponsor?</h3>
            <p className="lwp-confirm-text">
              Are you sure you want to delete <strong>"{confirmDeleteSpon.name}"</strong>? This cannot be undone.
            </p>
            <div className="lwp-modal-actions">
              <button className="lwp-btn-secondary" onClick={() => setConfirmDeleteSpon(null)} disabled={deleting}>Cancel</button>
              <button className="lwp-btn-danger" onClick={handleDeleteSponsor} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
